import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/ui/Header";
import ProgressHeader from "../../components/ui/ProgressHeader";
import DocumentUploadSection from "./components/DocumentUploadSection";
import UploadProgressSummary from "./components/UploadProgressSummary";
import DocumentValidationGuide from "./components/DocumentValidationGuide";
import Icon from "../../components/AppIcon";
import registrationService from "../../utils/registrationService";

const DocumentUploadManagement = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState({
    collegeId: null,
    passportPhoto: null,
    resume: null,
  });
  const [errors, setErrors] = useState({});
  const [showGuide, setShowGuide] = useState(false);

  // Check if user has completed previous steps
  useEffect(() => {
    const registrationData = localStorage.getItem("registrationFormData");
    if (!registrationData) {
      navigate("/profile-selection-landing");
      return;
    }

    // Load any previously uploaded documents
    const savedDocuments = localStorage.getItem("solvarsity_registration_documents");
    if (savedDocuments) {
      try {
        setDocuments(JSON.parse(savedDocuments));
      } catch (error) {
        console.error("Error loading saved documents:", error);
      }
    }
  }, [navigate]);

  // Save documents to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("solvarsity_registration_documents", JSON.stringify(documents));
  }, [documents]);

  const documentSections = [
    {
      key: "collegeId",
      title: "College ID Proof",
      description:
        "Upload a clear photo of your valid ID issued by your college (This college ID will be used to verify admission.)",
      acceptedFormats: ["jpg", "jpeg", "png", "pdf"],
      maxSize: 5,
      allowCamera: true,
      isRequired: false,
    },
    {
      key: "passportPhoto",
      title: "Passport Size Photo",
      description:
        "Upload a recent passport-size photograph with plain background",
      acceptedFormats: ["jpg", "jpeg", "png"],
      maxSize: 2,
      allowCamera: true,
      isRequired: false,
    },
    {
      key: "resume",
      title: "Resume/CV",
      description: "Upload your updated resume in PDF or Word format",
      acceptedFormats: ["pdf", "doc", "docx"],
      maxSize: 5,
      allowCamera: false,
      isRequired: false,
    },
  ];

  const handleFileSelect = async (sectionKey, fileData, error) => {
    if (error) {
      setErrors((prev) => ({ ...prev, [sectionKey]: error }));
      return;
    }

    setDocuments((prev) => ({
      ...prev,
      [sectionKey]: {
        file: fileData,
        name: fileData.name,
        size: fileData.size,
        type: fileData.type,
        url: URL.createObjectURL(fileData),
        uploadedAt: new Date(),
        status: 'uploaded'
      },
    }));

    // Clear any previous errors for this section
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[sectionKey];
      return newErrors;
    });

    // Save file to DB via service
    try {
      const result = await registrationService.uploadDocument(
        sectionKey,
        fileData
      );
      if (result.success) {
        console.log(`${sectionKey} uploaded successfully`, result.data);
      } else {
        console.error(`Failed to upload ${sectionKey}:`, result.error);
      }
    } catch (err) {
      console.error(`Unexpected error uploading ${sectionKey}:`, err);
    }
  };

  const handleFileRemove = (sectionKey) => {
    setDocuments((prev) => ({
      ...prev,
      [sectionKey]: null,
    }));

    // Clear any errors for this section
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[sectionKey];
      return newErrors;
    });
  };

  const handleBack = () => {
    navigate("/multi-step-registration-form");
  };

  const handleContinue = () => {
    // Validate all required documents are uploaded
    const missingDocs = documentSections
      .filter((section) => section.isRequired)
      .filter(
        (section) =>
          !documents[section.key] ||
          documents[section.key].status !== "uploaded"
      );

    if (missingDocs.length > 0) {
      const newErrors = {};
      missingDocs.forEach((doc) => {
        newErrors[doc.key] = "This document is required to proceed";
      });
      setErrors(newErrors);
      return;
    }

    // Save documents and proceed to payment
    localStorage.setItem(
      "solvarsity_registration_documents",
      JSON.stringify(documents)
    );
    navigate("/payment-processing");
  };

  const getCompletionStats = () => {
    const requiredSections = documentSections.filter(
      (section) => section.isRequired || !section.isRequired
    );
    const completed = requiredSections.filter(
      (section) =>
        documents[section.key] && documents[section.key].status === "uploaded"
    ).length;

    return {
      completed,
      total: requiredSections.length,
      percentage: Math.round((completed / requiredSections.length) * 100),
    };
  };

  const stats = getCompletionStats();

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ProgressHeader />

      <main className="pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-4">
              Document Upload
            </h1>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Please upload the required documents to complete your
              registration. All documents will be securely stored and verified
              within 24 hours.
            </p>
          </div>

          {/* Progress Overview */}
          <div className="mb-8">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-foreground">
                  Upload Progress
                </h2>
                <button
                  onClick={() => setShowGuide(!showGuide)}
                  className="flex items-center space-x-2 text-sm text-primary hover:text-primary/80 transition-smooth"
                >
                  <Icon name="Info" size={16} />
                  <span>{showGuide ? "Hide" : "Show"} Guidelines</span>
                </button>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-secondary">Overall Progress</span>
                  <span className="font-medium text-foreground">
                    {stats.completed}/{stats.total} documents
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-smooth duration-500 ${
                      stats.percentage === 100 ? "bg-success" : "bg-primary"
                    }`}
                    style={{ width: `${stats.percentage}%` }}
                  ></div>
                </div>
                <div className="text-right">
                  <span
                    className={`text-sm font-medium ${
                      stats.percentage === 100 ? "text-success" : "text-primary"
                    }`}
                  >
                    {stats.percentage}% Complete
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Guidelines */}
          {showGuide && (
            <div className="mb-8">
              <DocumentValidationGuide />
            </div>
          )}

          {/* Document Upload Sections */}
          <div className="space-y-8 mb-8">
            {documentSections.map((section) => (
              <DocumentUploadSection
                key={section.key}
                title={section.title}
                description={section.description}
                acceptedFormats={section.acceptedFormats}
                maxSize={section.maxSize}
                allowCamera={section.allowCamera}
                isRequired={section.isRequired}
                file={documents[section.key]}
                error={errors[section.key]}
                onFileSelect={(fileData, error) =>
                  handleFileSelect(section.key, fileData, error)
                }
                onFileRemove={() => handleFileRemove(section.key)}
              />
            ))}
          </div>

          {/* Upload Progress Summary */}
          <div className="mb-8">
            <UploadProgressSummary
              documents={documents}
              onBack={handleBack}
              onContinue={handleContinue}
            />
          </div>

          {/* Security Notice */}
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <Icon
                name="Shield"
                size={20}
                color="var(--color-primary)"
                className="mt-0.5 flex-shrink-0"
              />
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-primary">
                  Secure Document Handling
                </h3>
                <p className="text-sm text-text-secondary">
                  Your documents are encrypted using 256-bit SSL encryption and
                  stored securely. We follow strict data protection protocols
                  and never share your personal information with unauthorized
                  parties. All uploaded documents are automatically deleted
                  after the verification process is complete.
                </p>
                <div className="flex items-center space-x-4 text-xs text-text-secondary mt-3">
                  <div className="flex items-center space-x-1">
                    <Icon name="Lock" size={12} color="var(--color-success)" />
                    <span>256-bit SSL Encrypted</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon
                      name="Shield"
                      size={12}
                      color="var(--color-success)"
                    />
                    <span>GDPR Compliant</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Clock" size={12} color="var(--color-success)" />
                    <span>24hr Verification</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DocumentUploadManagement;
